import React, {
  useContext, useEffect, useCallback, useRef,
} from 'react';
import {
  FlatList,
  Platform,
  Animated,
} from 'react-native';
import { NativeViewGestureHandler } from 'react-native-gesture-handler'; // eslint-disable-line import/no-extraneous-dependencies

import { DISTANCE_TO_DECIDE_RESPONDER, SCROLL_EVENT_THROTTLE } from '../constants';
import { logHandlerEvent } from '../utils';
import { renderItem } from './DraggableFlatListRow';
import { DraggableFlatListContext } from '../contexts/DraggableFlatListContext';
import { ScrollViewContext } from '../contexts/ScrollViewContext';
import { DragAndDropContext, STATUS } from '../contexts/DragAndDropContext';


const FlatListComponent = (props) => {
  const {
          backgroundColor,
          data,
          itemHeight,
          keyExtractor,
          longPressGestureRef,
          scrollViewComponent,
          scrollViewGestureRef,
          verticalPanGestureRef,
          scrollContentOffsetY,
        } = useContext(DraggableFlatListContext),
        {
          isScrolling,
          isUserScrolling,
          onContentSizeChange,
          onMomentumScrollEnd,
          onScrollBeginDrag,
          contentSize,
          layout,
          onScrollEndDrag,
          onScrollLayout,
          contentOffset,
          setIsScrolling,
          setIsUserScrolling,
          startedScrollingAtY,
        } = useContext(ScrollViewContext),
        onScrollEvent = useRef(Animated.event([{
          nativeEvent: {
            contentOffset: {
              y: scrollContentOffsetY.current,
            },
          },
        }],
        {
          useNativeDriver: true,
        })),
        { touchStatus } = useContext(DragAndDropContext),
        scrollEnabled = touchStatus === STATUS.INACTIVE || touchStatus === STATUS.TOUCHED,
        FL = Platform.OS === 'web'
          ? FlatList
          : Animated.FlatList;


  useEffect(() => {
    const onScrollWeb = (/* event */) => {
      const doc = window.document.documentElement,
            y = (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);

      contentOffset.current = { x: 0, y };
    };

    if (Platform.OS === 'web') {
      window.addEventListener('scroll', onScrollWeb);
    }
    return () => {
      if (Platform.OS === 'web') {
        window.removeEventListener('scroll', onScrollWeb);
      }
    };
  }, [contentOffset]);

  useEffect(() => {
    const listener = ({ nativeEvent }) => {
      const { y } = nativeEvent.contentOffset;
      contentOffset.current = { x: 0, y };
      if (!isScrolling && isUserScrolling
        && Math.abs(y - startedScrollingAtY.current) > DISTANCE_TO_DECIDE_RESPONDER
      ) {
        setIsScrolling(true);
      }
    };

    Animated.forkEvent(onScrollEvent.current, listener);

    return () => Animated.unforkEvent(onScrollEvent.current, listener);
  }, [contentOffset,
    isScrolling, isUserScrolling, onScrollEvent, setIsScrolling, startedScrollingAtY]);

  useEffect(() => {
    const bottom = contentSize.height - layout.height;
    if (Platform.OS === 'ios' && !scrollEnabled && (contentOffset.current.y < 0 || contentOffset.current.y > bottom)) {
      const node = scrollViewComponent.current.getNode();
      node.scrollToOffset({
        offset: contentOffset.current.y < 0 ? 0 : bottom,
        animated: true,
      });
    }
  }, [contentOffset, contentSize.height, layout.height, scrollEnabled, scrollViewComponent]);

  const onHandlerStateChange = useCallback(({ nativeEvent }) => {
    logHandlerEvent('scrollViewComponent', { nativeEvent });
    if (nativeEvent.state === 2) {
      setIsUserScrolling(true);
    }

    if ([0, 1, 3, 5].includes(nativeEvent.state)) {
      setIsUserScrolling(false);
    }
  }, [setIsUserScrolling]);

  const getItemLayout = useCallback((_, index) => (
    { length: itemHeight, offset: itemHeight * index, index }
  ), [itemHeight]);

  const onContentSizeChanged = useCallback((width, height) => {
    onContentSizeChange(width, height);

    // fix for weird behaviour when deleting last item in list
    if (Platform.OS === 'ios') {
      const node = scrollViewComponent.current.getNode();
      node.scrollToOffset({
        offset: contentOffset.current.y,
        animated: true,
      });
    }
  }, [contentOffset, onContentSizeChange, scrollViewComponent]);

  return (
    <NativeViewGestureHandler
      ref={scrollViewGestureRef}
      simultaneousHandlers={[
        verticalPanGestureRef,
        longPressGestureRef,
      ]}
      onHandlerStateChange={onHandlerStateChange}
    >
      <FL
        ref={scrollViewComponent}
        removeClippedSubviews
        data={data}
        getItemLayout={getItemLayout}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        scrollEnabled={scrollEnabled}
        scrollEventThrottle={SCROLL_EVENT_THROTTLE}
        style={{ backgroundColor }}
        testID='flatList'
        windowSize={15}
        onContentSizeChange={onContentSizeChanged}
        onLayout={onScrollLayout}
        onMomentumScrollEnd={onMomentumScrollEnd}
        onScroll={onScrollEvent.current}
        onScrollBeginDrag={onScrollBeginDrag}
        onScrollEndDrag={onScrollEndDrag}
        {...props}
      />
    </NativeViewGestureHandler>
  );
};


export default FlatListComponent;
